/**
 * Oxbo-P client — the domain operational record (events, daily reports, cost,
 * NPT, plan-vs-actual, inbox, saved queries). Read-through cache; Oxbo-Z owns
 * none of it and never recomputes a KPI — every computed number (days,
 * pct_complete, rop_avg, variance, npt roll-ups) is served by P.
 *
 * Base URL is VITE_OXBO_P_URL (p.dev.oxbo.energy by default). MSW serves mocks
 * in standalone mode. Endpoints mirror the live contract exactly.
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { asArray } from "./mappers";
import { getDevToken } from "./devAuth";
import { getProdToken } from "./prodAuth";
import type {
  EventOut,
  EventCreate,
  ImportWorkoverResult,
  ImportWorkoverArg,
  DailyReportOut,
  DailyReportCreate,
  DailyReportUpdate,
  ReportCloseResponse,
  CostPivotResponse,
  CostPivotDimension,
  NptRollupResponse,
  PlanVsActualResponse,
  InboxResponse,
  SavedQueryOut,
  AfeLineOut,
  AfeLineCreate,
  ActivityOut,
  ActivityCreate,
  AfeLineUpdate,
  SavedQueryCreate,
  SurveyStationOut,
  TubularsResponse,
  BhaRunsResponse,
  ReportMudOut,
  MudCheckOut,
  SafetyDayOut,
  EventSafetyRollupOut,
  PadRollupOut,
  PadSharedCostsResponse,
  PadSharedCostCreate,
  PadSharedCostUpdate,
  PlanTimelineResponse,
  ArchMeta,
  WellReportRecency,
  CostLineOut,
  CostLineCreate,
} from "./types";

const P_BASE_URL = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

/**
 * The Bearer token to attach to Oxbo-P calls. Prefers the real-login prod token
 * (VITE_PROD_AUTH build); falls back to the dev-minted token (VITE_DEV_AUTH
 * build). In a mocks-only build both are null and no Authorization header is
 * sent (MSW answers the call anyway). Mirrors mApi exactly so P can validate the
 * JWT and scope by org — without a token P falls back to its demo identity.
 */
function getAuthToken(): string | null {
  return getProdToken() ?? getDevToken();
}

/**
 * Flatten the live grouped surveys response `{ passes: [{stations:[]}],
 * ungrouped: [] }` into a single MD-ordered `SurveyStationOut[]`. Tolerates a
 * plain array (mock/legacy) and any non-array field. Sorted by MD so the panel
 * and trajectory read in depth order.
 */
function flattenSurveys(raw: unknown): SurveyStationOut[] {
  if (Array.isArray(raw)) return raw as SurveyStationOut[];
  const r = (raw ?? {}) as { passes?: unknown; ungrouped?: unknown };
  const out: SurveyStationOut[] = [];
  const passes = Array.isArray(r.passes) ? r.passes : [];
  for (const pass of passes) {
    const stations = (pass as { stations?: unknown })?.stations;
    if (Array.isArray(stations)) out.push(...(stations as SurveyStationOut[]));
  }
  if (Array.isArray(r.ungrouped)) out.push(...(r.ungrouped as SurveyStationOut[]));
  return out.sort((a, b) => (a.md ?? 0) - (b.md ?? 0));
}

/**
 * Query params the live `/v1/inbox` accepts (§10.0, merged on main). `from`/`to`
 * are inclusive date bounds (YYYY-MM-DD); `mine` scopes to the caller's own
 * reports (server reads the JWT subject); `cursor` drives forward pagination.
 */
export interface InboxParams {
  state?: string;
  event_type?: string;
  well_uwi?: string;
  from?: string;
  to?: string;
  mine?: boolean;
  cursor?: string;
  limit?: number;
}

export const pApi = createApi({
  reducerPath: "pApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${P_BASE_URL}/v1`,
    // Inject the current JWT (real prod-login token, or dev-minted token) so P
    // can validate it and scope by org. In a mocks-only build both are null and
    // no Authorization header is sent (MSW answers the call anyway). Mirrors mApi.
    prepareHeaders: (headers) => {
      const token = getAuthToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  // Operational data is more volatile than master data — shorter cache window.
  keepUnusedDataFor: 60,
  tagTypes: [
    "Event",
    "Report",
    "Inbox",
    "SavedQuery",
    "Afe",
    "Survey",
    "Tubular",
    "Bha",
    "Mud",
    "Hse",
    "PadRollup",
    "PadSharedCost",
    "PlanTimeline",
    "Activity",
    "ArchMeta",
    "CostLine",
  ],
  endpoints: (builder) => ({
    // ── Events (jobs) ─────────────────────────────────────────────────────
    getWellEvents: builder.query<EventOut[], string>({
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/events`,
      transformResponse: asArray<EventOut>,
      providesTags: (result, _e, wellUwi) =>
        result
          ? [
              ...result.map((ev) => ({ type: "Event" as const, id: ev.id })),
              { type: "Event" as const, id: `WELL-${wellUwi}` },
            ]
          : [{ type: "Event" as const, id: `WELL-${wellUwi}` }],
    }),
    /**
     * Create a new event (job) — `POST /v1/events` (Oxbo-P). Each POST is a new
     * event (no upsert); org/created_by come from the token and `id` is
     * server-generated. The "Crear Workover" wizard posts `event_type:"workover"`.
     * Invalidates the owning well's event list tag (`Event/WELL-${well_uwi}`) so
     * `getWellEvents` refetches and the new workover shows without a manual
     * refresh — the wizard then selects it and lands on its Parte diario.
     */
    createEvent: builder.mutation<EventOut, EventCreate>({
      query: (body) => ({ url: "/events", method: "POST", body }),
      invalidatesTags: (_r, _e, body) => [{ type: "Event", id: `WELL-${body.well_uwi}` }],
    }),
    /**
     * Import a filled workover template — `POST /v1/import/workover` (Oxbo-P),
     * multipart with field `file` = the .xlsx. The caller passes a raw
     * `FormData`; `fetchBaseQuery` forwards a FormData `body` untouched and the
     * browser sets the `multipart/form-data` boundary itself — we must NOT set
     * `Content-Type` (pApi's prepareHeaders only sets Authorization, never a
     * JSON content-type, so the Bearer token rides along and multipart works).
     * On success P creates the well (via M), the event, its daily reports,
     * activities, cost + AFE lines and any missing catalog entries, returning an
     * `ImportWorkoverResult` summary. We invalidate the resulting well's event
     * list so `getWellEvents` refetches; the wells LIST lives in mApi and is
     * invalidated by the caller (`mApi.util.invalidateTags`) after success.
     *
     * Pass `{ formData, dryRun: true }` for a PREVIEW: the mutation appends
     * `?dry_run=true` and the backend returns the same expanded summary plus
     * `warnings[]` WITHOUT writing (we never invalidate on a dry-run).
     */
    importWorkover: builder.mutation<ImportWorkoverResult, ImportWorkoverArg>({
      query: ({ formData, dryRun }) => ({
        url: dryRun ? "/import/workover?dry_run=true" : "/import/workover",
        method: "POST",
        body: formData,
      }),
      // A dry-run writes NOTHING — never invalidate on a preview (only a real
      // write should refetch the well's event list).
      invalidatesTags: (result, _e, arg) =>
        result && !arg.dryRun ? [{ type: "Event", id: `WELL-${result.well_uwi}` }] : [],
    }),

    // ── Daily reports ─────────────────────────────────────────────────────
    getEventReports: builder.query<DailyReportOut[], string>({
      query: (eventId) => `/events/${eventId}/reports`,
      transformResponse: asArray<DailyReportOut>,
      providesTags: (result, _e, eventId) =>
        result
          ? [
              ...result.map((r) => ({ type: "Report" as const, id: r.id })),
              { type: "Report" as const, id: `EVENT-${eventId}` },
            ]
          : [{ type: "Report" as const, id: `EVENT-${eventId}` }],
    }),
    getReport: builder.query<DailyReportOut, string>({
      query: (reportId) => `/reports/${reportId}`,
      providesTags: (_r, _e, id) => [{ type: "Report", id }],
    }),
    // Latest daily-report date per well — drives the Well Explorer default sort
    // (active wells, by most-recent operational activity, first). Tolerant: on
    // error the caller falls back to the register's natural order.
    getWellsReportRecency: builder.query<WellReportRecency[], void>({
      query: () => `/wells/report-recency`,
      transformResponse: asArray<WellReportRecency>,
    }),
    createReport: builder.mutation<DailyReportOut, DailyReportCreate>({
      query: (body) => ({ url: "/reports", method: "POST", body }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: "Report", id: `EVENT-${result.event_id}` },
              { type: "Inbox", id: "LIST" },
            ]
          : [{ type: "Inbox", id: "LIST" }],
    }),
    updateReport: builder.mutation<DailyReportOut, { id: string; body: DailyReportUpdate }>({
      query: ({ id, body }) => ({ url: `/reports/${id}`, method: "PATCH", body }),
      invalidatesTags: (result, _e, { id }) =>
        result
          ? [
              { type: "Report", id },
              { type: "Report", id: `EVENT-${result.event_id}` },
            ]
          : [{ type: "Report", id }],
    }),
    closeReport: builder.mutation<ReportCloseResponse, { id: string; isLastOfEvent?: boolean }>({
      query: ({ id, isLastOfEvent }) => ({
        url: `/reports/${id}/close`,
        method: "POST",
        body: { is_last_of_event: isLastOfEvent ?? false },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Report", id },
        { type: "Inbox", id: "LIST" },
      ],
    }),

    // ── Time-log activities (§ time-entry) — the day's operational time log ─
    // The IADC-style entry grid reads/creates/edits/deletes these against the
    // live Oxbo-P contract: flat GET /reports/{id}/activities → list[ActivityOut]
    // (from v_activity_active), POST same, PATCH/DELETE /activities/{id}. The read
    // + all three writes share the Activity/REPORT-{id} tag so a create/edit/delete
    // re-fetches the flat list and the grid reflects the change immediately. This
    // is a distinct consumer from the Planner timeline (compute nodes, other shape).
    getReportActivities: builder.query<ActivityOut[], string>({
      query: (reportId) => `/reports/${reportId}/activities`,
      transformResponse: asArray<ActivityOut>,
      providesTags: (_r, _e, reportId) => [{ type: "Activity", id: `REPORT-${reportId}` }],
    }),
    createActivity: builder.mutation<ActivityOut, { reportId: string; body: ActivityCreate }>({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/activities`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { reportId }) => [
        { type: "Activity", id: `REPORT-${reportId}` },
        { type: "Report", id: reportId },
      ],
    }),
    // PATCH/DELETE key off the STABLE entity_id (Pattern-L), NOT the version-row
    // id — the version id changes on every edit, so keying by it would target a
    // stale row. entity_id is what the ActivityOut carries for exactly this.
    updateActivity: builder.mutation<
      ActivityOut,
      { entityId: string; reportId: string; body: ActivityCreate }
    >({
      query: ({ entityId, body }) => ({ url: `/activities/${entityId}`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { reportId }) => [{ type: "Activity", id: `REPORT-${reportId}` }],
    }),
    deleteActivity: builder.mutation<ActivityOut, { entityId: string; reportId: string }>({
      query: ({ entityId }) => ({ url: `/activities/${entityId}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, { reportId }) => [{ type: "Activity", id: `REPORT-${reportId}` }],
    }),

    // ── Server-computed roll-ups (display-only; never recomputed on the FE) ─
    getCostPivot: builder.query<
      CostPivotResponse,
      { eventId: string; dimension?: CostPivotDimension }
    >({
      query: ({ eventId, dimension }) => ({
        url: `/events/${eventId}/cost-pivot`,
        params: dimension ? { dimension } : {},
      }),
    }),
    getPlanVsActual: builder.query<PlanVsActualResponse, string>({
      query: (eventId) => `/events/${eventId}/plan-vs-actual`,
    }),
    getNptRollup: builder.query<NptRollupResponse, string>({
      query: (eventId) => `/events/${eventId}/npt-rollup`,
    }),

    // ── Day cost lines (FB2) — per-day cost entry on a daily report ───────
    // Unlike the server-computed cost roll-ups above (read-only), these are
    // user-entered: the day's costs the user loads from the Cost tab. Keyed by
    // the daily report id. `cost_date` is the COST date (ARCH §8 off-by-one, it
    // surfaces on the NEXT day's report). Endpoints are being built in parallel
    // in oxbo-p; this is coded to the agreed contract.
    getReportCostLines: builder.query<CostLineOut[], string>({
      transformResponse: asArray<CostLineOut>,
      query: (reportId) => `/reports/${reportId}/cost-lines`,
      providesTags: (result, _e, reportId) =>
        result
          ? [
              ...result.map((l) => ({ type: "CostLine" as const, id: l.id })),
              { type: "CostLine" as const, id: `REPORT-${reportId}` },
            ]
          : [{ type: "CostLine" as const, id: `REPORT-${reportId}` }],
    }),
    createCostLine: builder.mutation<CostLineOut, { reportId: string; body: CostLineCreate }>({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/cost-lines`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { reportId }) => [{ type: "CostLine", id: `REPORT-${reportId}` }],
    }),
    deleteCostLine: builder.mutation<void, { id: string; reportId: string }>({
      query: ({ id }) => ({ url: `/cost-lines/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, { reportId }) => [{ type: "CostLine", id: `REPORT-${reportId}` }],
    }),

    // ── AFE lines (§11) — the line grid + accrual method ──────────────────
    // The grid reads these; create/edit go straight to P, which recomputes the
    // roll-ups (committed / accrued / actual / variance) and returns the line —
    // the FE never recomputes accrual (§21f rule lives server-side).
    getAfeLines: builder.query<AfeLineOut[], string>({
      transformResponse: asArray<AfeLineOut>,
      query: (eventId) => `/events/${eventId}/afe-lines`,
      providesTags: (result, _e, eventId) =>
        result
          ? [
              ...result.map((l) => ({ type: "Afe" as const, id: l.id })),
              { type: "Afe" as const, id: `EVENT-${eventId}` },
            ]
          : [{ type: "Afe" as const, id: `EVENT-${eventId}` }],
    }),
    createAfeLine: builder.mutation<AfeLineOut, { eventId: string; body: AfeLineCreate }>({
      query: ({ eventId, body }) => ({
        url: `/events/${eventId}/afe-lines`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [{ type: "Afe", id: `EVENT-${eventId}` }],
    }),
    updateAfeLine: builder.mutation<
      AfeLineOut,
      { eventId: string; entityId: string; body: AfeLineUpdate }
    >({
      query: ({ eventId, entityId, body }) => ({
        url: `/events/${eventId}/afe-lines/${entityId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId, entityId }) => [
        { type: "Afe", id: entityId },
        { type: "Afe", id: `EVENT-${eventId}` },
      ],
    }),
    // ── Surveys (§5) — directional stations, keyed by well UWI ────────────
    // Trajectory columns (tvd/ns/ew/dls) are server-computed (min-curvature on
    // the OXBO-x compute-server); the FE reads them read-only and never
    // recomputes them. Returns the well's station list in MD order.
    getWellSurveys: builder.query<SurveyStationOut[], string>({
      // The live route returns the GROUPED shape `{ passes: [{stations:[]}],
      // ungrouped: [] }`, not a flat SurveyStationOut[]. Flatten passes'
      // stations + ungrouped into one MD-ordered station list. A plain array
      // (mock/legacy) passes through unchanged.
      transformResponse: flattenSurveys,
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/surveys`,
      providesTags: (result, _e, wellUwi) =>
        result
          ? [
              ...result.map((s) => ({ type: "Survey" as const, id: s.id })),
              { type: "Survey" as const, id: `WELL-${wellUwi}` },
            ]
          : [{ type: "Survey" as const, id: `WELL-${wellUwi}` }],
    }),
    // ── Tubulars (§6) — casing/assembly strings + pipe tally, per event ───
    getEventTubulars: builder.query<TubularsResponse, string>({
      query: (eventId) => `/events/${eventId}/tubulars`,
      providesTags: (_r, _e, eventId) => [{ type: "Tubular", id: eventId }],
    }),
    // ── BHA / Bit runs (§7) — runs + nested assembly + bit-ops grid ───────
    getEventBhaRuns: builder.query<BhaRunsResponse, string>({
      query: (eventId) => `/events/${eventId}/bha-runs`,
      providesTags: (_r, _e, eventId) => [{ type: "Bha", id: `EVENT-${eventId}` }],
    }),
    // ── Mud / fluids (§8 / §10.4-b) ───────────────────────────────────────
    // A report's day (checks + products + transactions + volumes) and an
    // event's property-check trend. The FE displays; product line totals are
    // qty × unit_cost (formatting only, no server KPI recomputed).
    getReportMud: builder.query<ReportMudOut, string>({
      query: (reportId) => `/reports/${reportId}/mud`,
      providesTags: (_r, _e, reportId) => [{ type: "Mud", id: `REPORT-${reportId}` }],
    }),
    getEventMudTrend: builder.query<MudCheckOut[], string>({
      transformResponse: asArray<MudCheckOut>,
      query: (eventId) => `/events/${eventId}/mud-checks`,
      providesTags: (_r, _e, eventId) => [{ type: "Mud", id: `EVENT-${eventId}` }],
    }),
    // ── HSE / safety (§9) ─────────────────────────────────────────────────
    // Safety is a DAILY HEADER (LTI counters + day checks), not per-observation
    // action rows. Report endpoint = one day; event endpoint = LTI rollup + the
    // daily HSE trail. All counters are server-computed; the FE only renders.
    getReportSafety: builder.query<SafetyDayOut | null, string>({
      query: (reportId) => `/reports/${reportId}/safety`,
      // The wire wraps the report's day header in `{ items: SafetyOut[] }`; the
      // panel wants the single day (first item), or null when none.
      transformResponse: (raw: { items?: SafetyDayOut[] } | SafetyDayOut) => {
        if (raw && "items" in raw) return raw.items?.[0] ?? null;
        return (raw as SafetyDayOut) ?? null;
      },
      providesTags: (_r, _e, reportId) => [{ type: "Hse", id: `REPORT-${reportId}` }],
    }),
    getEventSafety: builder.query<EventSafetyRollupOut, string>({
      query: (eventId) => `/events/${eventId}/safety`,
      // Guarantee `items` is an array so the trail never crashes on a partial body.
      transformResponse: (raw: EventSafetyRollupOut) => ({
        ...(raw ?? ({} as EventSafetyRollupOut)),
        items: Array.isArray(raw?.items) ? raw.items : [],
      }),
      providesTags: (_r, _e, eventId) => [{ type: "Hse", id: `EVENT-${eventId}` }],
    }),
    // ── Plan timeline (§13 / §13a / §19) — multi-track plan+actual per level ─
    // Composed (Z) from Oxbo-P events/activities (actual) + program/plan table
    // (planned) + Oxbo-M program-step/phase + Site/Pad grouping. Each level has
    // its own scope key: Fleet = all CPE units × pads over a horizon; Pad =
    // site_id; Well = uwi; Event = event_id (section/stage/interval ops).
    getFleetPlanTimeline: builder.query<PlanTimelineResponse, { division?: string } | void>({
      query: (params) => ({ url: "/fleet/plan-timeline", params: params ?? {} }),
      providesTags: [{ type: "PlanTimeline", id: "FLEET" }],
    }),
    getPadPlanTimeline: builder.query<PlanTimelineResponse, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/plan-timeline`,
      providesTags: (_r, _e, siteId) => [{ type: "PlanTimeline", id: `PAD-${siteId}` }],
    }),
    getWellPlanTimeline: builder.query<PlanTimelineResponse, string>({
      query: (uwi) => `/wells/${encodeURIComponent(uwi)}/plan-timeline`,
      providesTags: (_r, _e, uwi) => [{ type: "PlanTimeline", id: `WELL-${uwi}` }],
    }),
    getEventPlanTimeline: builder.query<PlanTimelineResponse, string>({
      query: (eventId) => `/events/${encodeURIComponent(eventId)}/plan-timeline`,
      providesTags: (_r, _e, eventId) => [{ type: "PlanTimeline", id: `EVENT-${eventId}` }],
    }),

    // ── Inbox / saved queries (cross-well) ────────────────────────────────
    // Cursor-paginated cross-well feed. A change to any FILTER starts a fresh
    // list; a change to `cursor` alone appends the next page onto the same cache
    // entry (serializeQueryArgs drops `cursor`, merge concatenates), so the
    // screen renders one growing list without re-fetching earlier pages.
    getInbox: builder.query<InboxResponse, InboxParams | void>({
      query: (params) => ({ url: "/inbox", params: params ?? {} }),
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { cursor: _cursor, ...filters } = queryArgs ?? {};
        return `${endpointName}(${JSON.stringify(filters)})`;
      },
      merge: (current, incoming, { arg }) => {
        // A fresh (no-cursor) load replaces; a paged load appends.
        if (!arg || !arg.cursor) {
          return incoming;
        }
        current.items.push(...incoming.items);
        current.next_cursor = incoming.next_cursor;
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: [{ type: "Inbox", id: "LIST" }],
    }),
    getSavedQueries: builder.query<SavedQueryOut[], void>({
      transformResponse: asArray<SavedQueryOut>,
      query: () => "/saved-queries",
      providesTags: [{ type: "SavedQuery", id: "LIST" }],
    }),
    createSavedQuery: builder.mutation<SavedQueryOut, SavedQueryCreate>({
      query: (body) => ({ url: "/saved-queries", method: "POST", body }),
      invalidatesTags: [{ type: "SavedQuery", id: "LIST" }],
    }),
    deleteSavedQuery: builder.mutation<void, string>({
      query: (id) => ({ url: `/saved-queries/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "SavedQuery", id: "LIST" }],
    }),

    // ── Pad view (§12) — per-well time+cost rollup, shared/pad cost lines
    //    (with server-computed allocation splits), and the pad Gantt timeline.
    //    Every number (allocated_shared, per-well split, utilization) is
    //    computed by Oxbo-P; the FE only lays it out.
    getPadRollup: builder.query<PadRollupOut, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/rollup`,
      providesTags: (_r, _e, siteId) => [{ type: "PadRollup", id: siteId }],
    }),
    getPadSharedCosts: builder.query<PadSharedCostsResponse, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/shared-costs`,
      providesTags: (_r, _e, siteId) => [{ type: "PadSharedCost", id: siteId }],
    }),
    createPadSharedCost: builder.mutation<
      PadSharedCostsResponse,
      { siteId: string; body: PadSharedCostCreate }
    >({
      query: ({ siteId, body }) => ({
        url: `/pads/${encodeURIComponent(siteId)}/shared-costs`,
        method: "POST",
        body,
      }),
      // A shared line changes the pad's per-well allocation, so both roll-ups refetch.
      invalidatesTags: (_r, _e, { siteId }) => [
        { type: "PadSharedCost", id: siteId },
        { type: "PadRollup", id: siteId },
      ],
    }),
    updatePadSharedCost: builder.mutation<
      PadSharedCostsResponse,
      { siteId: string; lineId: string; body: PadSharedCostUpdate }
    >({
      query: ({ siteId, lineId, body }) => ({
        url: `/pads/${encodeURIComponent(siteId)}/shared-costs/${lineId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { siteId }) => [
        { type: "PadSharedCost", id: siteId },
        { type: "PadRollup", id: siteId },
      ],
    }),
    // The pad Gantt (§12/§13) — multi-track plan+actual for the pad's CPE + wells.
    getPadTimeline: builder.query<PlanTimelineResponse, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/timeline`,
    }),

    // ── ARCH Reporte Diario — manual regulatory fields (§4-7) ─────────────
    // The manual fields that aren't operational data, keyed by daily_report_id
    // and owned by the backend arch_report_meta table. GET returns defaults
    // (objetivo seeded from event.objective_1) when none persisted; PUT persists
    // the editing-window save. The derived numbers (daily cost, totales, días,
    // TOTAL horas) are NOT here — the FE reads those from the daily's own
    // activities / server-computed KPIs, and the xlsx endpoints recompute them
    // authoritatively server-side.
    getArchMeta: builder.query<ArchMeta, string>({
      query: (dailyReportId) => `/daily-reports/${dailyReportId}/arch-meta`,
      providesTags: (_r, _e, dailyReportId) => [{ type: "ArchMeta", id: dailyReportId }],
    }),
    updateArchMeta: builder.mutation<ArchMeta, { dailyReportId: string; body: ArchMeta }>({
      query: ({ dailyReportId, body }) => ({
        url: `/daily-reports/${dailyReportId}/arch-meta`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { dailyReportId }) => [{ type: "ArchMeta", id: dailyReportId }],
    }),
  }),
});

export const {
  useGetWellEventsQuery,
  useCreateEventMutation,
  useImportWorkoverMutation,
  useGetEventReportsQuery,
  useGetReportQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useCloseReportMutation,
  useGetCostPivotQuery,
  useGetPlanVsActualQuery,
  useGetNptRollupQuery,
  useGetReportCostLinesQuery,
  useCreateCostLineMutation,
  useDeleteCostLineMutation,
  useGetReportActivitiesQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useGetAfeLinesQuery,
  useCreateAfeLineMutation,
  useUpdateAfeLineMutation,
  useGetWellSurveysQuery,
  useGetEventTubularsQuery,
  useGetEventBhaRunsQuery,
  useGetReportMudQuery,
  useGetEventMudTrendQuery,
  useGetReportSafetyQuery,
  useGetEventSafetyQuery,
  useGetInboxQuery,
  useGetSavedQueriesQuery,
  useCreateSavedQueryMutation,
  useDeleteSavedQueryMutation,
  useGetPadRollupQuery,
  useGetPadSharedCostsQuery,
  useCreatePadSharedCostMutation,
  useUpdatePadSharedCostMutation,
  useGetPadTimelineQuery,
  useGetFleetPlanTimelineQuery,
  useGetPadPlanTimelineQuery,
  useGetWellPlanTimelineQuery,
  useGetEventPlanTimelineQuery,
  useGetArchMetaQuery,
  useUpdateArchMetaMutation,
  useGetWellsReportRecencyQuery,
} = pApi;
